import { createFillRenderer } from "../gl/fillRenderer";
import { createLineRenderer } from "../gl/lineRenderer";
import { createDrawingsModel } from "./drawingsModel";
import { createDrawingPointer } from "./drawingPointer";
import { createDrawingEdits } from "./drawingEdits";
import { createDrawingPersistence } from "./drawingPersistence";
import { resolveShape } from "./drawingShape";
import { drawDrawingGeometry } from "./drawingGeometry";
import { drawDrawingHandles, drawDrawingPrices } from "./drawingText";
import { drawDrawingLevelLabels } from "./drawingLevelLabels";

/**
 * Drawings of the user (engine.addDrawing & co.): state (drawingsModel),
 * pointer (drawingPointer), saving per symbol (drawingPersistence) and drawing on the layers. Position lines live in
 * their own layer (positionsState), so they are never counted or removed here.
 *
 * @param {object} params
 * @param {{ colors: object, palette: object, fontFamily: string }} params.theme
 * @param {() => { store: object, barDurationMs: number, timeScale: object, priceScale: object, chartWidth: number }} params.getView -
 *   read when needed, the bars and scales change all the time; chartWidth: with the price axis, CSS px
 * @param {() => void} params.invalidate - schedules a frame
 * @param {() => void} params.redrawText - redraws the text layer only
 * @param {object} params.storage - where the drawings of a symbol are kept, see drawingPersistence
 */
export const createDrawings = ({ theme, getView, invalidate, redrawText, storage }) => {
  const fillRenderer = createFillRenderer();
  const lineRenderer = createLineRenderer();
  const model = createDrawingsModel({ theme, invalidate, redrawText });
  const persistence = createDrawingPersistence({ model, storage });

  const itemShapes = (pane) =>
    model.items
      .filter((item) => item.isVisible)
      .map((item) => resolveShape(item, getView(), pane))
      .filter(Boolean);

  return {
    renderers: [fillRenderer, lineRenderer],

    subscribe: model.subscribe,
    list: model.list,
    select: model.select,
    remove: model.remove,
    removeAll: model.removeAll,
    setSymbol: persistence.setSymbol,

    destroy: () => {
      persistence.destroy();
      model.destroy();
    },

    ...createDrawingEdits({ model, getView }),
    ...createDrawingPointer({ model, itemShapes, getView }),

    /** @param {{ viewport: number[], pane: object, pixelRatio: number }} params - inside the clip of the price pane */
    draw: ({ viewport, pane, pixelRatio }) =>
      drawDrawingGeometry({ fillRenderer, lineRenderer, shapes: itemShapes(pane), viewport, pane, pixelRatio }),

    /**
     * @param {object} textLayer
     * @param {{ pane: object, decimals: number }} params
     */
    drawPrices: (textLayer, params) =>
      drawDrawingPrices(textLayer, itemShapes(params.pane), {
        ...params,
        priceScale: getView().priceScale,
        fontFamily: theme.fontFamily,
      }),

    /**
     * @param {object} textLayer
     * @param {{ pane: object, decimals: number }} params
     */
    drawLevelLabels: (textLayer, params) =>
      drawDrawingLevelLabels(textLayer, itemShapes(params.pane), {
        ...params,
        priceScale: getView().priceScale,
        fontFamily: theme.fontFamily,
      }),

    /**
     * @param {object} textLayer
     * @param {{ width: number, height: number }} pane
     */
    drawHandles: (textLayer, pane) => drawDrawingHandles(textLayer, itemShapes(pane), pane, theme.colors.drawingHandle.css),
  };
};
